import {
  ArrowDownwardRounded,
  ArrowUpwardRounded,
  CheckRounded,
  CloseRounded,
  FullscreenExitRounded,
  FullscreenRounded,
  LinkOutlined,
  LockOutlined,
  TagOutlined,
} from "@mui/icons-material";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  Tooltip,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import PropTypes from "prop-types";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import SearchInput from "../inputs/SearchInput";
import Flx from "../layout/Flx";
import Txt from "../typography/Txt";
import { isDocHidden } from "../../_src_shared/utils/functions/isDocHidden";
import { filterAssociationFields } from "./config/associationFieldsConfig";

const countTotalSelected = (sel) =>
  Object.values(sel || {}).reduce(
    (n, arr) => n + (Array.isArray(arr) ? arr.length : 0),
    0,
  );

const countCategoriesWithSelections = (sel) =>
  Object.values(sel || {}).filter((a) => Array.isArray(a) && a.length > 0)
    .length;

const AssociationsEditor = ({
  open,
  loan,
  initial = {},
  existing = {},
  onSave,
  onClose,
  enabledFields,
  hostLabel,
  title = "Edit associations",
  saving = false,
  // Search text the dialog opens with (e.g. a doc type, so a per-type
  // "Assign existing" lands on the matching files). The user can clear it.
  initialQuery = "",
}) => {
  const fields = useMemo(
    () => filterAssociationFields(enabledFields),
    [enabledFields],
  );

  const [activeKey, setActiveKey] = useState(fields[0]?.key || "");
  const [query, setQuery] = useState("");
  const [fullScreen, setFullScreen] = useState(false);
  const [selected, setSelected] = useState(initial);
  // Sort state for the documents view header. Multi-column: an ordered
  // array of `{ column, dir }` entries — first entry sorts first, later
  // entries break ties. Empty array keeps source order. Only applied
  // when activeKey is "loanDocuments". Default mirrors LoanFilesTable's
  // "Category & Type" grouped sort: docGroup A→Z then docType A→Z.
  const [docSort, setDocSort] = useState([
    { column: "docGroup", dir: "asc" },
    { column: "docType", dir: "asc" },
  ]);

  // Click cycle per column: not in sort → asc → desc → removed. Adding
  // a new column appends it to the end (lower precedence than columns
  // already in the sort), so users can layer sorts without losing them.
  const cycleDocSort = useCallback((column) => {
    setDocSort((prev) => {
      const idx = prev.findIndex((s) => s.column === column);
      if (idx === -1) return [...prev, { column, dir: "asc" }];
      const next = [...prev];
      if (next[idx].dir === "asc") {
        next[idx] = { column, dir: "desc" };
        return next;
      }
      return next.filter((s) => s.column !== column);
    });
  }, []);

  // When only a single category is enabled (e.g. AssignLoanFilesButton passes
  // ["loanDocuments"]) we drop the left tab rail and let the results list
  // take the full dialog width. Anything more than one category keeps the
  // two-column layout so the user can switch between them.
  const singleField = fields.length === 1;

  // Reset internal state only when the dialog transitions from
  // closed → open. Re-running on every `initial` / `fields` ref change
  // would clobber the user's tab selection mid-session whenever the
  // parent re-renders (e.g. a parent passing `initial={{}}` literal).
  const wasOpenRef = useRef(false);
  useEffect(() => {
    if (open && !wasOpenRef.current) {
      setSelected(initial || {});
      setQuery(initialQuery || "");
      setActiveKey(fields[0]?.key || "");
    }
    wasOpenRef.current = open;
  }, [open, initial, fields, initialQuery]);

  const activeField = useMemo(
    () => fields.find((f) => f.key === activeKey) || fields[0],
    [fields, activeKey],
  );

  const results = useMemo(() => {
    if (!activeField) return [];
    let items = activeField.getSource(loan) || [];
    // Hidden loan documents are never offered as association options.
    if (activeField.key === "loanDocuments") {
      items = items.filter((r) => !isDocHidden(r));
    }
    const q = query ? query.toLowerCase() : null;
    const filtered = q
      ? items.filter((r) => {
          const label = (activeField.getLabel(r) || "").toLowerCase();
          const sub = (activeField.getSublabel(r) || "").toLowerCase();
          return label.includes(q) || sub.includes(q);
        })
      : items;
    // Apply alphabetical sort only on the documents view. Walk each
    // active sort entry in precedence order; the first non-zero
    // comparison wins, ties cascade to the next entry.
    if (activeField.key === "loanDocuments" && docSort.length > 0) {
      const valueOf = (column, r) => {
        if (column === "docGroup") return (r?.docGroup || "").toLowerCase();
        if (column === "docType") return (r?.docType || "").toLowerCase();
        return (activeField.getLabel(r) || "").toLowerCase();
      };
      return [...filtered].sort((a, b) => {
        for (const { column, dir } of docSort) {
          const dirVal = dir === "asc" ? 1 : -1;
          const av = valueOf(column, a);
          const bv = valueOf(column, b);
          // Empty values always sink to the bottom regardless of dir,
          // so "—" rows don't cluster at the top in desc mode.
          if (!av && bv) return 1;
          if (av && !bv) return -1;
          if (av < bv) return -1 * dirVal;
          if (av > bv) return 1 * dirVal;
        }
        return 0;
      });
    }
    return filtered;
  }, [loan, activeField, query, docSort]);

  // Set of ids that are already saved against the host record. These render
  // pre-checked + disabled — the editor's only job is picking what to ADD,
  // so we don't let the user toggle them off here.
  const lockedIdsByField = useMemo(() => {
    const map = {};
    for (const f of fields) {
      map[f.key] = new Set(existing?.[f.key] || []);
    }
    return map;
  }, [fields, existing]);

  const lockedIdsActive = lockedIdsByField[activeField?.key] || new Set();

  const toggle = useCallback(
    (fieldKey, id) => {
      const locked = lockedIdsByField[fieldKey];
      if (locked?.has(id)) return;
      setSelected((prev) => {
        const current = new Set(prev?.[fieldKey] || []);
        if (current.has(id)) current.delete(id);
        else current.add(id);
        return { ...prev, [fieldKey]: [...current] };
      });
    },
    [lockedIdsByField],
  );

  const handleSave = useCallback(() => {
    onSave?.(selected);
  }, [onSave, selected]);

  const totalSelected = countTotalSelected(selected);
  const totalCategories = countCategoriesWithSelections(selected);

  if (!activeField) return null;

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      fullScreen={fullScreen}
      maxWidth={false}
      PaperProps={{
        sx: {
          borderRadius: fullScreen ? 0 : 2,
          overflow: "hidden",
          border: `1px solid ${grey[200]}`,
          ...(fullScreen
            ? {}
            : {
                width: "min(1280px, 95vw)",
                height: "90vh",
                maxWidth: "none",
              }),
        },
      }}
    >
      <Flx
        ac
        g={1}
        sx={{
          px: 2,
          py: 1.5,
          borderBottom: `1px solid ${grey[200]}`,
          bgcolor: grey[50],
        }}
      >
        <LinkOutlined sx={{ fontSize: 16, color: grey[700] }} />
        <Txt fontSize={14} fw={600} sx={{ color: grey[900] }}>
          {title}
        </Txt>
        {hostLabel && (
          <Txt fontSize={12} sx={{ color: grey[600] }}>
            on {hostLabel}
          </Txt>
        )}
        <Flx ac g={0.5} sx={{ ml: "auto" }}>
          <Tooltip title={fullScreen ? "Exit full screen" : "Full screen"} arrow>
            <IconButton
              size="small"
              onClick={() => setFullScreen((v) => !v)}
              disabled={saving}
            >
              {fullScreen ? (
                <FullscreenExitRounded sx={{ fontSize: 18 }} />
              ) : (
                <FullscreenRounded sx={{ fontSize: 18 }} />
              )}
            </IconButton>
          </Tooltip>
          <IconButton size="small" onClick={onClose} disabled={saving}>
            <CloseRounded sx={{ fontSize: 18 }} />
          </IconButton>
        </Flx>
      </Flx>

      <Box
        sx={{
          px: 2,
          py: 1,
          borderBottom: `1px solid ${grey[200]}`,
        }}
      >
        <Flx ac g={1}>
          <Box sx={{ flex: 1 }}>
            <SearchInput
              value={query}
              onChange={setQuery}
              placeholder={`Search ${activeField.label.toLowerCase()}…`}
              size="small"
              variant="text"
              suppressBorder
              autoFocus
              fullWidth
            />
          </Box>
          {totalSelected > 0 && (
            <Box
              sx={{
                px: 1,
                py: 0.25,
                borderRadius: 999,
                bgcolor: grey[900],
                color: "#fff",
              }}
            >
              <Txt fontSize={11} fw={600} sx={{ color: "#fff" }}>
                {totalSelected}
              </Txt>
            </Box>
          )}
        </Flx>
      </Box>

      <DialogContent
        sx={{
          p: 0,
          display: "grid",
          gridTemplateColumns: singleField ? "1fr" : "220px 1fr",
          flex: 1,
          minHeight: 0,
        }}
      >
        {!singleField && (
          <Box
            sx={{
              borderRight: `1px solid ${grey[200]}`,
              bgcolor: grey[50],
              // Left rail is static — the category list is short and should
              // stay fixed while only the results table (right) scrolls.
              overflowY: "hidden",
              py: 0.5,
            }}
          >
            {fields.map((f) => {
            const lockedCount = lockedIdsByField[f.key]?.size || 0;
            const newCount = selected?.[f.key]?.length || 0;
            const count = lockedCount + newCount;
            const active = f.key === activeKey;
            const Icon = f.icon;
            const sourceLen = (f.getSource(loan) || []).length;
            return (
              <Box
                key={f.key}
                onClick={() => {
                  setActiveKey(f.key);
                  setQuery("");
                }}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  px: 1.5,
                  py: 0.75,
                  cursor: "pointer",
                  bgcolor: active ? "#fff" : "transparent",
                  boxShadow: active ? `inset 2px 0 0 0 ${grey[900]}` : "none",
                  "&:hover": active ? {} : { bgcolor: "rgba(255,255,255,0.6)" },
                }}
              >
                <Icon
                  sx={{
                    fontSize: 15,
                    color: active ? f.color : grey[500],
                  }}
                />
                <Txt
                  fontSize={12.5}
                  fw={active ? 600 : 500}
                  sx={{
                    color: active ? grey[900] : grey[700],
                    flex: 1,
                  }}
                >
                  {f.label}
                </Txt>
                <Tooltip title={`${count} selected of ${sourceLen}`} arrow>
                  <Box
                    sx={{
                      px: 0.75,
                      py: 0.125,
                      borderRadius: 999,
                      bgcolor:
                        count > 0
                          ? active
                            ? f.color
                            : grey[300]
                          : "transparent",
                      color: count > 0 && active ? "#fff" : grey[700],
                      minWidth: 18,
                      textAlign: "center",
                    }}
                  >
                    <Txt
                      fontSize={10}
                      fw={600}
                      sx={{
                        color: count > 0 && active ? "#fff" : grey[700],
                      }}
                    >
                      {count > 0 ? count : sourceLen}
                    </Txt>
                  </Box>
                </Tooltip>
              </Box>
            );
          })}
          </Box>
        )}

        <Box sx={{ overflowY: "auto", minHeight: 0 }}>
          {/* Documents view: column header strip with a sortable Name column
              and a Doc Type column. Only renders for loanDocuments — other
              categories keep their original headerless list. */}
          {activeField.key === "loanDocuments" && results.length > 0 && (
            <Box
              sx={{
                position: "sticky",
                top: 0,
                zIndex: 1,
                display: "grid",
                gridTemplateColumns:
                  "26px minmax(0, 1fr) 128px 148px 60px",
                alignItems: "center",
                gap: 1.5,
                px: 2,
                py: 1,
                bgcolor: grey[50],
                borderBottom: `1px solid ${grey[200]}`,
              }}
            >
              <Box />
              <SortableHeader
                label="Name"
                column="name"
                docSort={docSort}
                onClick={() => cycleDocSort("name")}
              />
              <SortableHeader
                label="Category"
                column="docGroup"
                docSort={docSort}
                onClick={() => cycleDocSort("docGroup")}
              />
              <SortableHeader
                label="Doc Type"
                column="docType"
                docSort={docSort}
                onClick={() => cycleDocSort("docType")}
              />
              <Box />
            </Box>
          )}
          {results.length === 0 ? (
            <Flx column ac jc="center" g={1} sx={{ p: 5, color: grey[500] }}>
              <TagOutlined sx={{ fontSize: 22, opacity: 0.4 }} />
              <Txt fontSize={13} sx={{ color: grey[500] }}>
                {query ? (
                  <>
                    No matches for "
                    <Box component="span" sx={{ color: grey[800] }}>
                      {query}
                    </Box>
                    "
                  </>
                ) : (
                  `No ${activeField.label.toLowerCase()} on this loan`
                )}
              </Txt>
            </Flx>
          ) : (
            results.map((r) => {
              const id = r._id;
              const isLocked = lockedIdsActive.has(id);
              const isPicked =
                !isLocked &&
                (selected?.[activeField.key] || []).includes(id);
              const checked = isLocked || isPicked;
              const isDocsView = activeField.key === "loanDocuments";
              return (
                <Tooltip
                  key={id}
                  title={
                    isLocked
                      ? "Already linked — open the linked record to remove"
                      : ""
                  }
                  placement="left"
                  arrow
                  disableHoverListener={!isLocked}
                  disableFocusListener={!isLocked}
                  disableTouchListener={!isLocked}
                >
                  <Box
                    onClick={
                      isLocked ? undefined : () => toggle(activeField.key, id)
                    }
                    sx={{
                      // Documents view uses a grid that aligns with the
                      // sticky column header above (checkbox / name /
                      // docGroup / docType / linked badge). Other
                      // categories keep the simpler flex row layout.
                      display: isDocsView ? "grid" : "flex",
                      gridTemplateColumns: isDocsView
                        ? "26px minmax(0, 1fr) 128px 148px 60px"
                        : undefined,
                      alignItems: "center",
                      gap: isDocsView ? 1.5 : 1.5,
                      px: 2,
                      py: 1,
                      cursor: isLocked ? "not-allowed" : "pointer",
                      borderBottom: `1px solid ${grey[100]}`,
                      bgcolor: isLocked
                        ? `${activeField.color}0A`
                        : isPicked
                          ? grey[50]
                          : "transparent",
                      opacity: isLocked ? 0.7 : 1,
                      "&:hover": isLocked ? {} : { bgcolor: grey[50] },
                    }}
                  >
                    <Flx
                      ac
                      jc="center"
                      sx={{
                        width: 18,
                        height: 18,
                        borderRadius: 0.5,
                        border: checked ? "none" : `1px solid ${grey[400]}`,
                        bgcolor: checked ? activeField.color : "#fff",
                        flexShrink: 0,
                      }}
                    >
                      {checked && (
                        <CheckRounded
                          sx={{ fontSize: 14, color: "#fff", strokeWidth: 3 }}
                        />
                      )}
                    </Flx>
                    <Box sx={{ flex: isDocsView ? undefined : 1, minWidth: 0 }}>
                      <Txt
                        fontSize={13}
                        title={activeField.getLabel(r) || undefined}
                        sx={{
                          color: grey[900],
                          display: "block",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {activeField.getLabel(r)}
                      </Txt>
                      {!isDocsView && activeField.getSublabel(r) && (
                        <Txt
                          fontSize={11}
                          title={activeField.getSublabel(r) || undefined}
                          sx={{
                            color: grey[600],
                            display: "block",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {activeField.getSublabel(r)}
                        </Txt>
                      )}
                    </Box>
                    {isDocsView && (
                      <Txt
                        fontSize={12}
                        title={r?.docGroup || undefined}
                        sx={{
                          color: r?.docGroup ? grey[700] : grey[400],
                          fontStyle: r?.docGroup ? "normal" : "italic",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          minWidth: 0,
                        }}
                      >
                        {r?.docGroup || "—"}
                      </Txt>
                    )}
                    {isDocsView && (
                      <Txt
                        fontSize={12}
                        title={r?.docType || undefined}
                        sx={{
                          color: r?.docType ? grey[700] : grey[400],
                          fontStyle: r?.docType ? "normal" : "italic",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          minWidth: 0,
                        }}
                      >
                        {r?.docType || "—"}
                      </Txt>
                    )}
                    {isLocked && (
                      <Flx
                        ac
                        g={0.4}
                        sx={{
                          flexShrink: 0,
                          px: 0.75,
                          py: 0.125,
                          borderRadius: 0.75,
                          bgcolor: `${activeField.color}1A`,
                          color: activeField.color,
                        }}
                      >
                        <LockOutlined sx={{ fontSize: 11 }} />
                        <Txt
                          fontSize={10}
                          fw={700}
                          sx={{
                            color: activeField.color,
                            textTransform: "uppercase",
                            letterSpacing: "0.04em",
                          }}
                        >
                          Linked
                        </Txt>
                      </Flx>
                    )}
                  </Box>
                </Tooltip>
              );
            })
          )}
        </Box>
      </DialogContent>

      <Flx
        ac
        jb
        g={1}
        sx={{
          px: 2,
          py: 1.5,
          borderTop: `1px solid ${grey[200]}`,
          bgcolor: grey[50],
        }}
      >
        <Txt fontSize={12} sx={{ color: grey[600] }}>
          {totalSelected === 0
            ? "Nothing selected yet."
            : `${totalSelected} record${totalSelected === 1 ? "" : "s"} across ${totalCategories} categor${totalCategories === 1 ? "y" : "ies"}.`}
        </Txt>
        <Flx ac g={1}>
          <Button
            size="medium"
            onClick={onClose}
            variant="outlined"
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            size="medium"
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            startIcon={
              saving ? (
                <CircularProgress size={12} sx={{ color: "#fff" }} />
              ) : null
            }
          >
            {saving ? "Saving…" : "Save"}
          </Button>
        </Flx>
      </Flx>
    </Dialog>
  );
};

// Clickable column header for the documents view. Cycles its parent's
// sort state via `onClick`, shows a direction arrow when this column
// participates, and surfaces a small precedence badge (1, 2, …) when
// more than one column is sorted so users can tell which sort runs
// first.
const SortableHeader = ({ label, column, docSort, onClick }) => {
  const sortIndex = (docSort || []).findIndex((s) => s.column === column);
  const isActive = sortIndex !== -1;
  const dir = isActive ? docSort[sortIndex].dir : null;
  const showPrecedence = isActive && (docSort?.length || 0) > 1;
  return (
    <Box
      onClick={onClick}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
        cursor: "pointer",
        userSelect: "none",
        color: isActive ? grey[900] : grey[700],
        "&:hover": { color: grey[900] },
      }}
    >
      <Txt
        fontSize={11}
        fw={700}
        sx={{
          color: "inherit",
          textTransform: "uppercase",
          letterSpacing: "0.04em",
        }}
      >
        {label}
      </Txt>
      {dir === "asc" && <ArrowUpwardRounded sx={{ fontSize: 12 }} />}
      {dir === "desc" && <ArrowDownwardRounded sx={{ fontSize: 12 }} />}
      {showPrecedence && (
        <Box
          component="span"
          sx={{
            fontSize: "9px",
            fontWeight: 700,
            lineHeight: 1,
            minWidth: 14,
            height: 14,
            px: 0.4,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "50%",
            bgcolor: grey[200],
            color: grey[800],
          }}
        >
          {sortIndex + 1}
        </Box>
      )}
    </Box>
  );
};

AssociationsEditor.propTypes = {
  open: PropTypes.bool.isRequired,
  loan: PropTypes.object.isRequired,
  initial: PropTypes.object,
  // Records already linked to the host. Pre-checks them and disables
  // toggling — the editor can only ADD new associations.
  existing: PropTypes.object,
  onSave: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  enabledFields: PropTypes.arrayOf(PropTypes.string),
  hostLabel: PropTypes.string,
  title: PropTypes.string,
  saving: PropTypes.bool,
  initialQuery: PropTypes.string,
};

export default AssociationsEditor;
